import guestAxios from "../helpers/guestAxios";
import instance from "../helpers/axios";
import { instance as rawAxios } from "../hooks/axios";
import {
  CLUB_CASHIER_INVITE,
  CLUB_CASHIER_RESEND_INVITE,
  CLUB_CASHIERS_FOR_CLUB,
  CLUB_OPERATORS_FOR_CLUB,
  CLUB_EMPLOYEE_INVITE,
  CLUB_EMPLOYEE_RESEND_INVITE,
  CLUB_EMPLOYEES_FOR_CLUB,
  CLUB_MANAGER_ACTIVITY,
  CLUB_MANAGER_INVITE,
  CLUB_MANAGER_RESEND_INVITE,
  CLUB_REPORTS_EVENT_SUMMARY_DOWNLOAD,
  CLUB_REPORTS_SALES,
  CLUB_REPORTS_SALES_BOOKINGS,
  CLUB_REPORTS_SALES_ISSUED_TICKETS,
  CLUB_REPORTS_TABLE_BOOKING_LINES,
  CLUB_CATALOG_ITEM,
  CLUB_CATALOG_ITEMS,
  CLUB_MANAGERS_FOR_CLUB,
  CLUB_MANAGER_SHARE_LINK,
  CLUB_PUBLIC_BOOK_LATEST,
  CLUB_PUBLIC_BY_SLUG,
  CLUB_WEBSITE_UPDATE,
} from "./endPoint";
import { formatAxiosErrorMessage } from "./httpError";
import {
  assertBlobIsNotJsonError,
  coerceDownloadBlob,
  parseContentDispositionFilename,
} from "../helpers/fileDownload";
import {
  buildAuthorizationHeader,
  readStoredAccessToken,
} from "../helpers/authToken";
import { normalizeEventSummaryReadiness } from "../helpers/eventSummaryReadiness";

/**
 * Admin only. POST JSON (camelCase keys only):
 * { email, name, clubId } — clubId = 24-char Mongo ObjectId hex.
 * Same path under /api if REACT_APP_USE_API_PREFIX is set (see apiBase).
 */
/**
 * Club reusable catalog (add-ons + table preorder).
 * GET /club/:clubId/catalog-items?kind=&includeInactive=
 * Auth: ADMIN (any club) | CLUB_MANAGER (own club). ORGANISER: no catalog CRUD.
 */
export const fetchClubCatalogItems = (clubId, params = {}) =>
  instance.get(CLUB_CATALOG_ITEMS(clubId), { params });

/** POST /club/:clubId/catalog-items — 201 { message, data: item } */
export const createClubCatalogItem = (clubId, body) =>
  instance.post(CLUB_CATALOG_ITEMS(clubId), body);

/** PUT /club/:clubId/catalog-items/:itemId — any subset of fields */
export const updateClubCatalogItem = (clubId, itemId, body) =>
  instance.put(CLUB_CATALOG_ITEM(clubId, itemId), body);

/**
 * Admin only. POST JSON (camelCase keys only):
 * { email, name, clubId } — clubId = 24-char Mongo ObjectId hex.
 * Same path under /api if REACT_APP_USE_API_PREFIX is set (see apiBase).
 */
export const inviteClubManager = (body) =>
  instance.post(CLUB_MANAGER_INVITE, body);

/**
 * Activity / audit timeline. GET /club/manager/activity
 * Params: { page, limit, clubId? } — Admin may filter by clubId.
 * Club manager / employee: omit clubId (scoped by token); wrong clubId → 403.
 */
export const fetchClubManagerActivity = (params) =>
  instance.get(CLUB_MANAGER_ACTIVITY, { params });

/**
 * Club sales report. GET /club/reports/sales
 *
 * Query (camelCase; same Authorization header as other club calls):
 * - clubId — Admin may send when filtering one partner. Club manager / employee: omit (backend uses JWT managed club).
 * - eventId — Optional Mongo ObjectId string for one event; omit for all events.
 * - eventType — Optional ongoing | upcoming | past (omit for “All types”).
 * - dateFrom, dateTo — Optional YYYY-MM-DD; event start date range (Asia/Kolkata).
 * - saleChannel — Optional ONLINE or OFFLINE; omit for both.
 * - breakdown — Optional day | week | month | event (breakdownSeries in response).
 * - soldByUserId — Optional offline seller / table referral drill-down.
 * - guestlist — When true, guestlist rows only (CLUB_MANAGER | ADMIN; 403 for CLUB_EMPLOYEE).
 * - tableBookingsOnly — When true, table booking channel only (mutually exclusive with guestlist).
 *   Use tableBookings.summary for KPIs; ticket summary.overall is zeroed. Do not use
 *   GET /club/reports/sales/bookings for this mode — use fetchClubTableBookingReportLines.
 *
 * Response: summary.overall = all matched PAID orders;
 * summary.online = online + legacy rows without salechannel;
 * summary.offline = paid offline POS only (excludes guestlist);
 * summary.guestlist = complimentary guestlist orders;
 * byChannel = ONLINE, OFFLINE, ON_GROUND, GUESTLIST rows (channel, orderCount, ticketsSold, revenue);
 * catalogSnapshots = ticketAddonTotal, tableAddonTotal, preorderTotal, preorderExtraOnline.
 * Detail pages: same path with catalogSource, catalogKind, catalogPage, catalogLimit.
 * That response's catalogSnapshots includes page, limit, total, totalPages, and lines for the current page only.
 * byEvent.offlineTickets = paid offline only; byEvent.guestlistTickets = guestlist count;
 * offlineByEmployee excludes guestlist.
 * When eventId is set: byCategory is an array of per–ticket-type rows (null when no event filter).
 * Admin JWT only: adminFinancials — salesCount, orderCount, ticketsSold, overallSale, ticketSales,
 * commission (online tickets only), commissionBase, cgst, sgst, byEvent[], and event (when eventId query is set).
 * byEvent rows also expose onlineRevenue, offlineRevenue, and nested tableBookings
 * (bookingCount, guestsTotal, revenue, minimumSpendTotal, depositTotal, commission).
 * When tableBookingsOnly=true: tableBookings.summary + byEvent + byEmployee drive the UI;
 * byChannel and offlineByEmployee are empty.
 */
export const fetchClubSalesReport = (params) =>
  instance.get(CLUB_REPORTS_SALES, { params });

/**
 * Paginated booking rows for sales report filters.
 * GET /club/reports/sales/bookings — same filters as sales + page, limit (max 100).
 * Each item should include bookingref / bookingRef (e.g. FZ6JZKO). orderRef / orderid are internal ids (off_*).
 */
export const fetchClubSalesBookings = (params) =>
  instance.get(CLUB_REPORTS_SALES_BOOKINGS, { params });

/**
 * Offline POS rows with issued ticket + link. Same query params as bookings.
 * GET /club/reports/sales/issued-tickets — page, limit (max 100).
 */
export const fetchClubSalesIssuedTickets = (params) =>
  instance.get(CLUB_REPORTS_SALES_ISSUED_TICKETS, { params });

/**
 * Paginated table booking line items for sales report drill-down.
 * GET /club/reports/table-bookings/lines — same filters as sales + page, limit (max 100).
 * referredByUserId OR soldByUserId (alias).
 * Each item should include saleChannel / salechannel (ONLINE | OFFLINE) when available.
 */
export const fetchClubTableBookingReportLines = (params) =>
  instance.get(CLUB_REPORTS_TABLE_BOOKING_LINES, { params });

function clubApiAuthHeader() {
  const authorization = buildAuthorizationHeader(readStoredAccessToken());
  return authorization ? { Authorization: authorization } : {};
}

async function parseBlobErrorPayload(data) {
  if (!(data instanceof Blob)) return data;
  try {
    const text = await data.text();
    return JSON.parse(text);
  } catch {
    return data;
  }
}

/**
 * GET /club/reports/event-summary/:eventId/readiness
 * Auth: ADMIN | CLUB_MANAGER
 * @param {string} eventId
 */
export async function fetchClubEventSummaryReadiness(eventId) {
  const id = String(eventId ?? "").trim();
  if (!id) {
    throw new Error("Event id is required");
  }
  try {
    const response = await instance.get(
      `${CLUB_REPORTS_EVENT_SUMMARY_DOWNLOAD}/${encodeURIComponent(id)}/readiness`
    );
    return normalizeEventSummaryReadiness(response);
  } catch (error) {
    if (error instanceof Error && !error.response) {
      throw error.message;
    }
    throw formatAxiosErrorMessage(error);
  }
}

/**
 * POST /club/reports/event-summary/:eventId/force-close-device
 * Auth: ADMIN | CLUB_MANAGER
 * @param {string} eventId
 * @param {{ loginId: string; deviceId: string; reason: "lost" | "unavailable" | "force"; deactivateLogin?: boolean }} body
 */
export async function forceCloseClubEventSummaryDevice(
  eventId,
  { loginId, deviceId, reason, deactivateLogin }
) {
  const id = String(eventId ?? "").trim();
  if (!id) {
    throw new Error("Event id is required");
  }
  try {
    return await instance.post(
      `${CLUB_REPORTS_EVENT_SUMMARY_DOWNLOAD}/${encodeURIComponent(id)}/force-close-device`,
      {
        loginId,
        deviceId,
        reason,
        deactivateLogin:
          deactivateLogin != null ? deactivateLogin : reason === "lost",
      }
    );
  } catch (error) {
    if (error instanceof Error && !error.response) {
      throw error.message;
    }
    throw formatAxiosErrorMessage(error);
  }
}

/**
 * POST /club/reports/event-summary/:eventId/close
 * Auth: ADMIN | CLUB_MANAGER (club manager: own club only).
 * Sets eventstatus to Completed. Default setEndTimeToNow=true also updates enddatetime.
 * @param {string} eventId
 * @param {{ setEndTimeToNow?: boolean }} [body]
 */
export async function closeClubEvent(eventId, { setEndTimeToNow = true } = {}) {
  const id = String(eventId ?? "").trim();
  if (!id) {
    throw new Error("Event id is required");
  }
  try {
    return await instance.post(
      `${CLUB_REPORTS_EVENT_SUMMARY_DOWNLOAD}/${encodeURIComponent(id)}/close`,
      { setEndTimeToNow: Boolean(setEndTimeToNow) }
    );
  } catch (error) {
    if (error instanceof Error && !error.response) {
      throw error.message;
    }
    throw formatAxiosErrorMessage(error);
  }
}

/**
 * Download event summary file. GET /club/reports/event-summary/:eventId/download
 * Auth: ADMIN | CLUB_MANAGER (403 otherwise).
 * @param {string} eventId
 * @param {{ format?: "xlsx" | "csv" }} [options]
 * @returns {Promise<{ blob: Blob, filename: string | null, contentType: string | undefined }>}
 */
export async function downloadClubEventSummary(eventId, { format = "xlsx" } = {}) {
  const id = String(eventId ?? "").trim();
  if (!id) {
    throw new Error("Event id is required");
  }
  const fmt = format === "csv" ? "csv" : "xlsx";

  try {
    const response = await rawAxios.get(
      `${CLUB_REPORTS_EVENT_SUMMARY_DOWNLOAD}/${encodeURIComponent(id)}/download`,
      {
        params: { format: fmt },
        responseType: "blob",
        headers: clubApiAuthHeader(),
      }
    );
    const contentType = response.headers?.["content-type"];
    let blob = coerceDownloadBlob(response.data, contentType);
    blob = await assertBlobIsNotJsonError(blob, contentType);
    return {
      blob,
      filename: parseContentDispositionFilename(
        response.headers?.["content-disposition"]
      ),
      contentType,
    };
  } catch (error) {
    const status = error?.response?.status;
    let data = error?.response?.data;
    data = await parseBlobErrorPayload(data);
    if (error?.response) {
      error.response.data = data;
    }

    if (status === 409) {
      const message =
        (typeof data?.message === "string" && data.message.trim()) ||
        "Report download blocked until all scanner devices sync offline data and close the event.";
      const blocked = new Error(message);
      blocked.code = "EVENT_SUMMARY_BLOCKED";
      blocked.readiness = normalizeEventSummaryReadiness(data);
      throw blocked;
    }

    if (error instanceof Error && !error.response) {
      throw error.message;
    }
    throw formatAxiosErrorMessage(error);
  }
}

/**
 * Download tax invoice PDF. GET /club/reports/event-summary/:eventId/invoice
 * Auth: ADMIN only (403 for CLUB_MANAGER and others).
 * @param {string} eventId
 * @returns {Promise<{ blob: Blob, filename: string | null, contentType: string | undefined }>}
 */
export async function downloadClubEventInvoice(eventId) {
  const id = String(eventId ?? "").trim();
  if (!id) {
    throw new Error("Event id is required");
  }

  try {
    const response = await rawAxios.get(
      `${CLUB_REPORTS_EVENT_SUMMARY_DOWNLOAD}/${encodeURIComponent(id)}/invoice`,
      {
        responseType: "blob",
        headers: clubApiAuthHeader(),
      }
    );
    const contentType = response.headers?.["content-type"];
    let blob = coerceDownloadBlob(response.data, contentType);
    blob = await assertBlobIsNotJsonError(blob, contentType);
    return {
      blob,
      filename: parseContentDispositionFilename(
        response.headers?.["content-disposition"]
      ),
      contentType,
    };
  } catch (error) {
    const data = error?.response?.data;
    if (data instanceof Blob) {
      try {
        const text = await data.text();
        const parsed = JSON.parse(text);
        if (error.response) error.response.data = parsed;
      } catch {
        /* keep blob */
      }
    }
    if (error instanceof Error && !error.response) {
      throw error.message;
    }
    throw formatAxiosErrorMessage(error);
  }
}

/** POST /club/reports/sales/issued-tickets/:orderId/resend-link */
export const postIssuedTicketResendLink = (orderId) =>
  instance.post(
    `${CLUB_REPORTS_SALES_ISSUED_TICKETS}/${encodeURIComponent(
      String(orderId)
    )}/resend-link`
  );

/** Admin: managers assigned to a club */
export const fetchClubManagers = (clubId) =>
  instance.get(`${CLUB_MANAGERS_FOR_CLUB}${clubId}/managers`);

/** Admin or CLUB_MANAGER: mini-site only (branding + pages) */
export const updateClubWebsite = (clubId, body) =>
  instance.put(`${CLUB_WEBSITE_UPDATE}${clubId}`, body);

/** Public buyer-facing mini-site (auth per backend) */
export const fetchClubPublicBySlug = (slug) =>
  instance.get(`${CLUB_PUBLIC_BY_SLUG}${encodeURIComponent(slug)}`);

/** Same as fetchClubPublicBySlug but never sends Authorization (incognito / guest ticket page). */
export const fetchClubPublicBySlugGuest = (slug) =>
  guestAxios.get(`${CLUB_PUBLIC_BY_SLUG}${encodeURIComponent(slug)}`);

/**
 * GET /club/manager/club/:clubId/share-link?target=website|book-latest
 * Auth: ADMIN | CLUB_MANAGER — printable QR + share URLs.
 * @param {string} clubId
 * @param {{ target?: "website" | "book-latest" }} [options]
 */
export const fetchClubShareLink = (clubId, { target = "website" } = {}) =>
  instance.get(CLUB_MANAGER_SHARE_LINK(clubId), { params: { target } });

/** GET /club/public/:slug/book-latest — guest resolver for /clubs/:slug/book */
export const fetchClubBookLatestGuest = (slug) =>
  guestAxios.get(CLUB_PUBLIC_BOOK_LATEST(slug));

/**
 * POST /club/employee/invite — body { email, firstname, lastname, clubId }.
 * Auth: ADMIN | CLUB_MANAGER
 */
export const inviteClubEmployee = (body) =>
  instance.post(CLUB_EMPLOYEE_INVITE, body);

/** GET /club/employee/club/:clubId/employees */
export const fetchClubEmployees = (clubId) =>
  instance.get(`${CLUB_EMPLOYEES_FOR_CLUB}${clubId}/employees`);

/** POST /club/employee/resend-invite/:userId — Auth: ADMIN | CLUB_MANAGER */
export const postClubEmployeeResendInvite = (userId) =>
  instance.post(
    `${CLUB_EMPLOYEE_RESEND_INVITE}${encodeURIComponent(String(userId))}`
  );

/**
 * POST /club/cashier/invite — body { email, firstname, lastname, clubId }.
 * Auth: ADMIN | CLUB_MANAGER
 */
export const inviteClubCashier = (body) =>
  instance.post(CLUB_CASHIER_INVITE, body);

/** GET /club/cashier/club/:clubId/cashiers */
export const fetchClubCashiers = (clubId) =>
  instance.get(`${CLUB_CASHIERS_FOR_CLUB}${clubId}/cashiers`);

/** GET /club/operator/club/:clubId/operators */
export const fetchClubOperators = (clubId) =>
  instance.get(`${CLUB_OPERATORS_FOR_CLUB}${clubId}/operators`);

/** POST /club/cashier/resend-invite/:userId — Auth: ADMIN | CLUB_MANAGER */
export const postClubCashierResendInvite = (userId) =>
  instance.post(
    `${CLUB_CASHIER_RESEND_INVITE}${encodeURIComponent(String(userId))}`
  );

/** POST /club/manager/resend-invite/:userId — Auth: ADMIN */
export const postClubManagerResendInvite = (userId) =>
  instance.post(
    `${CLUB_MANAGER_RESEND_INVITE}${encodeURIComponent(String(userId))}`
  );
